import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import apiService from 'utils/apiService';

// ******** Get Documents For Job *********
export const getDocuments = async ({ pageParam = 1, ...q }) => {
  const { data } = await apiService.get('/api/light/documents/', {
    ...q,
    page: pageParam,
  });
  return data;
};

export const useDocuments = (q: Record<string, any>) => {
  return useInfiniteQuery(
    ['documents', q],
    ({ pageParam }) => getDocuments({ pageParam, ...q }),
    {
      getNextPageParam: (lastPage: any) => {
        return lastPage.next_page ?? undefined;
      },
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useDesktopDocuments = (q: Record<string, any>, page = 1) => {
  return useQuery(
    ['documents', q, { page }],
    () => getDocuments({ pageParam: page, ...q }),
    {
      staleTime: 15 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

const getAllDocuments = async (q: Record<string, any>) => {
  const { data } = await apiService.get('/api/light/documents/', q);
  return data;
};

export const useAllDocuments = (q: Record<string, any>) => {
  return useQuery(['all-documents', q], () => getAllDocuments(q), {
    staleTime: 5 * 60 * 1000,
  });
};

// ********* Get Document Detail *********
const getDocumentDetail = async (id: string) => {
  const { data } = await apiService.get(`/api/light/documents/${id}/`);
  return data;
};

export const useDocumentDetail = (id: string) => {
  return useQuery(['document-detail', id], () => getDocumentDetail(id), {
    staleTime: 15 * 60 * 1000,
  });
};

// ****** Upload Document ******
export const createDocument = async (payload: Record<string, any>) => {
  const { data } = await apiService.post('/api/light/documents/', payload);
  return data;
};

export const updateDocument = async (
  id: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(`/api/light/documents/${id}/`, payload);
  return data;
};

export const deleteDocument = async (
  id: string,
  q: Record<string, any> = {}
) => {
  await apiService.delete(`/api/light/documents/${id}/`, q);
  return true;
};

export const bulkSoftDeleteDocuments = async (ids: number[]) => {
  const { data } = await apiService.post('/api/documents/bulk_soft_delete/', {
    ids,
  });
  return data;
};

export const bulkDeleteDocuments = async (ids: number[]) => {
  const { data } = await apiService.post('/api/light/documents/bulk_delete/', {
    ids,
  });
  return data;
};

export const bulkDownloadDocuments = async (ids: number[]) => {
  const { data } = await apiService.post('/api/documents/bulk_zipfile/', {
    ids,
  });
  return data;
};

/**
 * Streams the (filtered) documents list as a CSV file. This is the standard
 * list endpoint with an `Accept: text/csv` header, so the export honours the
 * same query params (job, tags, search, date ranges, etc.) the user is
 * currently viewing. The caller decides the download filename.
 */
export const exportDocumentsCsv = async (q: Record<string, any> = {}) => {
  const response = await apiService.getBlob('/api/light/documents/', q, {
    Accept: 'text/csv',
  });
  return { blob: response.data as Blob };
};

export const combineDocuments = async (ids: number[]) => {
  const { data } = await apiService.post('/api/light/combined-document/', {
    docs: ids,
  });
  return data;
};

export const getCombinedDocument = async (id: string) => {
  const { data } = await apiService.get(`/api/light/combined-document/${id}/`);
  return data;
};

export const useCombinedDocument = (id: string) => {
  return useQuery(['combined-document', id], () => getCombinedDocument(id), {
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  });
};

const getDocumentSignerChoices = async (jobId: string) => {
  const { data } = await apiService.get(
    `/api/light/jobs/${jobId}/document_signer_choices/`
  );
  return data;
};

export const useDocumentSignerChoices = (jobId: string) => {
  return useQuery(
    ['document-signer-choices', jobId],
    () => getDocumentSignerChoices(jobId),
    {
      staleTime: 5 * 60 * 1000,
      enabled: !!jobId,
    }
  );
};

export type CombinedDocumentPayload = {
  id: number;
  name: string;
  description?: string;
  docs: { id: number; order_id: number; actions: object[] }[];
};

export type PhotoGridDocStub = {
  id: number;
  name: string;
  is_active: boolean;
  preview_url: string | null;
  doc_url: string | null;
};

/**
 * Lays out the selected photos in a grid PDF and appends it as the last source
 * document. Returns immediately (202) with a stub; `is_active`/`preview_url`/
 * `doc_url` are populated by a background task, so the caller must poll.
 */
export const generateDocFromPhotos = async (id: number, photos: number[]) => {
  const { data } = await apiService.post(
    `/api/light/combined-document/${id}/generate_doc_from_photos/`,
    { photos }
  );
  return data as PhotoGridDocStub;
};

export const updateCombinedDocument = async (
  id: number,
  payload: CombinedDocumentPayload
) => {
  const { data } = await apiService.patch(
    `/api/light/combined-document/${id}/`,
    payload
  );
  return data;
};

export const updateCombinedDocumentTags = async (
  id: number,
  tags: string[]
) => {
  const { data } = await apiService.patch(
    `/api/light/combined-document/${id}/`,
    { tags }
  );
  return data;
};

export const generateCombinedDocument = async (
  id: number,
  payload: CombinedDocumentPayload
) => {
  // PRISM-434: "Combine Documents" only saves the merged document to the job's
  // documents list. The signing email is sent separately (and per-recipient)
  // via `sendCombinedDocEmail` from the document table.
  const { data } = await apiService.post(
    `/api/light/combined-document/${id}/generate_document/`,
    payload
  );
  return data;
};

// ****** Combined document signing email (PRISM-434) ******
export type CombinedDocEmailFieldsConfig = {
  // Eligible recipients (customer and/or rep) who have signer placements.
  recipients: { value: number; label: string }[];
  // Prefilled HTML message body keyed by recipient user id (as a string).
  message: Record<string, string>;
  subject: string;
};

const getCombinedDocEmailFieldsConfig = async (id: number) => {
  const { data } = await apiService.get(
    `/api/light/combined-document/${id}/email_fields_config/`
  );
  return data as CombinedDocEmailFieldsConfig;
};

export const useCombinedDocEmailFieldsConfig = (id: number, enabled = true) => {
  return useQuery(
    ['combined-doc-email-fields-config', id],
    () => getCombinedDocEmailFieldsConfig(id),
    {
      // Message bodies embed a fresh signing link, so don't cache aggressively.
      staleTime: 0,
      enabled: enabled && !!id,
    }
  );
};

export type SendCombinedDocEmailPayload = {
  recipient: number;
  message: string;
  subject: string;
};

// Sends the signing email to a single recipient (customer or rep). The
// recipient must be one of the ids returned by `email_fields_config`.
export const sendCombinedDocEmail = async (
  id: number,
  payload: SendCombinedDocEmailPayload
) => {
  const { data } = await apiService.post(
    `/api/light/combined-document/${id}/send_email/`,
    payload
  );
  return data;
};

// ******* Import document ******
export const importDocTemplate = async (
  jobId: string,
  payload: Record<string, any>,
  q: Record<string, any> = {}
) => {
  const { data } = await apiService.post(
    `/api/light/jobs/${jobId}/import_doctemplate/`,
    payload,
    q
  );
  return data;
};

export const importCompanyDoc = async (
  jobId: string,
  payload: Record<string, any>,
  q: Record<string, any> = {}
) => {
  const { data } = await apiService.post(
    `/api/jobs/${jobId}/import_companydoc/`,
    payload,
    q
  );
  return data;
};

export const enableSignautre = async (id: number) => {
  const { data } = await apiService.post(
    `/api/light/documents/${id}/create_signed_document/`
  );
  return data;
};

export const impersonateAndSign = async (signerId: number) => {
  const { data } = await apiService.post(
    `/api/light/documentsigners/${signerId}/impersonate_and_sign/`
  );
  return data;
};

export const impersonateAndSignCombined = async (
  combinedDocId: string | number,
  signerId: number
) => {
  const { data } = await apiService.post(
    `/api/light/combined-document/${combinedDocId}/impersonate_and_sign_combined/`,
    {},
    { signer: signerId }
  );
  return data;
};

// ******** document signing ******
const getSignDocumentInfo = async (id: string, impersonatedBy?: string) => {
  const { data } = await apiService.get(
    `/api/light/signed-documents/${id}/template_info/`,
    impersonatedBy ? { impersonated_by: impersonatedBy } : {}
  );
  return data;
};

export const useSignDocumentInfo = (
  id: string,
  enabled = false,
  impersonatedBy?: string
) => {
  return useQuery(
    ['signed-document-info', id, impersonatedBy ?? ''],
    () => getSignDocumentInfo(id, impersonatedBy),
    {
      staleTime: 0,
      enabled,
    }
  );
};

const getSignDocumentMeta = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/signed-documents/${id}/meta/`
  );
  return data;
};

export const useSignDocumentMeta = (id: string) => {
  return useQuery(['signed-document-meta', id], () => getSignDocumentMeta(id), {
    staleTime: 0,
  });
};

export const updateCustomerDetails = async (
  id: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/light/signed-documents/${id}/update_customer/`,
    payload
  );
  return data;
};

export const voidSignatures = async (id: number) => {
  await apiService.delete(`/api/light/signed-documents/${id}/`);
  return true;
};

// ******** combined document signing (v2) ******
const getCombinedDocSigningMeta = async (
  id: string,
  impersonatedBy?: string
) => {
  const { data } = await apiService.get(
    `/api/light/combined-document/${id}/signing_meta/`,
    impersonatedBy ? { impersonated_by: impersonatedBy } : {}
  );
  return data;
};

export const useCombinedDocSigningMeta = (
  id: string,
  impersonatedBy?: string
) => {
  return useQuery(
    ['combined-doc-signing-meta', id, impersonatedBy ?? ''],
    () => getCombinedDocSigningMeta(id, impersonatedBy),
    {
      staleTime: 0,
      enabled: !!id,
    }
  );
};

export const acceptCombinedDocErsd = async (
  id: string,
  payload: {
    signer_source: string;
    props?: Record<string, any>;
  }
) => {
  const { data } = await apiService.post(
    `/api/light/combined-document/${id}/ersd/`,
    payload
  );
  return data;
};

/**
 * Mark a single placement as filled by the current signer. Called per-field
 * (signature, initials, date, checkbox) as the user completes each one.
 *
 * `placementId` is the action object's `id` from the signing meta response.
 * `value` is capped at 1000 chars by the backend; checkboxes send the string
 * "true" / "false" and date fields send their localised string.
 */
export const markCombinedDocFieldComplete = async (
  placementId: number,
  payload: { value: string; props?: Record<string, any> }
) => {
  const { data } = await apiService.post(
    `/api/light/combined-document-placements/${placementId}/field_completed/`,
    payload
  );
  return data;
};

export const completeCombinedDocSigning = async (
  id: string,
  payload: {
    signer_source: string;
    props?: Record<string, any>;
  }
) => {
  const { data } = await apiService.post(
    `/api/light/combined-document/${id}/completed/`,
    payload
  );
  return data as { message: string; redirect_url: string };
};

export const authenticateCombinedDocumentSigner = async (params: {
  key: string;
  signer: string;
}) => {
  const { data } = await apiService.get(
    '/api/guest/authenticate_combined_document_signer/',
    params
  );
  return data as { sid: string; redirect_url: string };
};

// ****** Email Job Document ******
const getEmailJobDocFieldsConfig = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/documents/${id}/email_job_doc_fields_config/`
  );
  return data;
};

export const useEmailJobDocFieldsConfig = (id: string) => {
  return useQuery(
    ['email-job-doc-fields-config', id],
    () => getEmailJobDocFieldsConfig(id),
    {
      staleTime: Infinity,
      enabled: !!id,
    }
  );
};

export const emailJobDoc = async (id: string, payload: FormData) => {
  const { data } = await apiService.post(
    `/api/light/documents/${id}/email_job_doc/`,
    payload
  );
  return data;
};

const getDocumentChoices = async ({ pageParam = 1, ...q }) => {
  const { data } = await apiService.get('/api/light/document_choices/', {
    ...q,
    page: pageParam,
  });
  return data;
};

export const useDocumentChoices = (q: Record<string, any>) => {
  return useInfiniteQuery(
    ['document-choices', q],
    ({ pageParam }) => getDocumentChoices({ pageParam, ...q }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.next_page ?? undefined;
      },
      staleTime: 15 * 60 * 1000,
    }
  );
};

// *********************** Get Document By Employeee ***************8

const getEmployeeDocs = async ({ ...q }) => {
  const { data } = await apiService.get('/api/light/documents/', {
    ...q,
  });
  return data;
};

export const useEmployeeDocuments = (q: Record<string, any>) => {
  return useInfiniteQuery(
    ['employee-docs', q],
    ({ pageParam }) => getEmployeeDocs({ pageParam, ...q }),
    {
      getNextPageParam: (lastPage: any) => {
        return lastPage.next_page ?? undefined;
      },
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const updateUserCredentials = async (
  payload: Record<string, any>,
  userId?: number
) => {
  apiService.updateContentType('multipart/form-data');
  const { data } = await apiService.patch(
    `/api/light/auth/update_credentials/?user=${userId}`,
    payload
  );
  apiService.updateContentType('application/json');
  return data;
};

export const markFieldComplete = async (
  signerId: number,
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/light/documentsigners/${signerId}/field_completed/`,
    payload
  );
  return data;
};

export const completeSigning = async (
  signerId: number,
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/light/documentsigners/${signerId}/completed/`,
    payload
  );

  return data;
};

export const attachSurveyToDocument = async (
  id: number,
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/light/signed-documents/${id}/attach_survey/`,
    payload
  );
  return data;
};
